/**
 * =============================================================================
 * FALCON - Module Deneigement des Parkings Avion
 * =============================================================================
 *
 * @file        ProtectedRoute.tsx
 * @description Composant de protection des routes avec gestion des roles
 *              Redirige vers /login si non authentifie ou /unauthorized si non autorise
 *
 * @usage       <Route element={<ProtectedRoute roles={['ROLE_ADMIN']}><AdminPage /></ProtectedRoute>} />
 *
 * @version     2.5
 * @author      Equipe FALCON
 * =============================================================================
 */

import { Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/store/authStore';
import { useAuthorization } from '@/hooks/useAuthorization';

interface ProtectedRouteProps {
  /** Contenu a proteger */
  children: React.ReactNode;
  /** Roles autorises (optionnel - si non specifie, seule l'authentification est requise) */
  roles?: string[];
  /** Si true, tous les roles sont requis (AND) au lieu d'un seul (OR) */
  requireAll?: boolean;
  /** URL de redirection personnalisee en cas de non-authentification */
  loginRedirect?: string;
  /** URL de redirection personnalisee en cas de non-autorisation */
  unauthorizedRedirect?: string;
}

/**
 * Composant de protection des routes avec gestion des roles
 *
 * Verifie l'authentification et optionnellement les roles de l'utilisateur.
 * - Si non authentifie -> redirection vers /login
 * - Si authentifie mais role insuffisant -> redirection vers /unauthorized
 *
 * @example
 * // Route protegee (authentification seule)
 * <ProtectedRoute>
 *   <Dashboard />
 * </ProtectedRoute>
 *
 * @example
 * // Route protegee avec role
 * <ProtectedRoute roles={['ROLE_ADMIN']}>
 *   <AdminPanel />
 * </ProtectedRoute>
 *
 * @example
 * // Route protegee avec plusieurs roles possibles
 * <ProtectedRoute roles={['ROLE_ADMIN', 'ROLE_REGULATEUR']}>
 *   <RegulationPage />
 * </ProtectedRoute>
 */
export function ProtectedRoute({
  children,
  roles,
  requireAll = false,
  loginRedirect = '/login',
  unauthorizedRedirect = '/unauthorized',
}: ProtectedRouteProps) {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const { hasAnyRole, hasAllRoles } = useAuthorization();
  const location = useLocation();

  // Verification de l'authentification
  if (!isAuthenticated) {
    // Sauvegarder l'URL demandee pour redirection apres login
    return <Navigate to={loginRedirect} state={{ from: location }} replace />;
  }

  // Verification des roles (si specifies)
  if (roles && roles.length > 0) {
    const hasPermission = requireAll ? hasAllRoles(roles) : hasAnyRole(roles);

    if (!hasPermission) {
      return <Navigate to={unauthorizedRedirect} replace />;
    }
  }

  return <>{children}</>;
}

export default ProtectedRoute;
